import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Code2, Zap, Shield, Globe, Layers, GitBranch, Rocket, Server,
  Database, Smartphone, Wifi, Gauge, Wrench, Plug, Activity, FileCode,
  ShoppingBag, Building2, Users, Package,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "react-nextjs": {
    eyebrow: "Web Development · Stacks",
    title: "React & Next.js Development",
    sub: "Modern React apps with SSR, edge rendering, and rock-solid DX.",
    metaDesc: "Custom React and Next.js web applications with SSR, edge rendering, and type-safe APIs.",
    intro:
      "We build polished React and Next.js applications — from marketing sites with perfect Lighthouse scores to complex App Router platforms with streaming SSR, server actions and edge middleware. Type-safe, testable, and tuned for Core Web Vitals.",
    features: [
      { icon: Zap, title: "Streaming SSR", description: "App Router with React Server Components and Suspense." },
      { icon: Code2, title: "Type-safe APIs", description: "End-to-end TypeScript with tRPC or typed server actions." },
      { icon: Globe, title: "Edge rendering", description: "Vercel Edge or Cloudflare Workers for global low latency." },
      { icon: Layers, title: "Design systems", description: "shadcn/ui, Tailwind tokens, and reusable component libraries." },
      { icon: Shield, title: "Auth & RLS", description: "NextAuth, Clerk or Supabase Auth with row-level security." },
      { icon: GitBranch, title: "Preview deploys", description: "Per-branch URLs, automated tests, and instant rollbacks." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Define goals, routes, data model and success metrics." },
      { step: "02", title: "Architecture", description: "App Router structure, caching, ISR and rendering strategy." },
      { step: "03", title: "Build", description: "Weekly sprints with preview URLs and live demos." },
      { step: "04", title: "Launch", description: "Performance hardening, monitoring and analytics." },
    ],
    deliverables: ["Production Next.js app", "Reusable component library", "CI/CD with preview envs", "Lighthouse 95+ scores", "Docs & onboarding", "30 days support"],
    techStack: ["React 19", "Next.js 15", "TypeScript", "Tailwind CSS", "shadcn/ui", "tRPC", "Prisma", "Vercel", "Cloudflare"],
    pricing: [
      { name: "Marketing site", price: "$3k", features: ["Up to 8 pages", "CMS-ready", "SEO + analytics", "2 weeks"] },
      { name: "Web app", price: "$14k", period: "project", highlighted: true, features: ["Auth + DB", "Dashboards", "API integrations", "6–10 weeks"] },
      { name: "Retainer", price: "$7k", period: "month", features: ["Dedicated engineer", "Weekly releases", "Priority fixes"] },
    ],
    faqs: [
      { q: "Pages Router or App Router?", a: "App Router by default for new projects. We migrate Pages Router apps incrementally." },
      { q: "Do you support React Native?", a: "Yes — we share TypeScript code and design tokens across web and mobile." },
    ],
  },
  "mern-stack": {
    eyebrow: "Web Development · Stacks",
    title: "MERN Stack Development",
    sub: "MongoDB, Express, React and Node — end-to-end JavaScript apps.",
    metaDesc: "Full-stack MERN development: MongoDB, Express, React, Node.js apps with REST and GraphQL APIs.",
    intro:
      "We deliver MERN apps from MVP to production scale. Express APIs with clean architecture, MongoDB schemas designed for queries you actually run, React frontends with modern state management, and deployment pipelines you can hand off to your team.",
    features: [
      { icon: Database, title: "MongoDB schemas", description: "Indexes, aggregation pipelines, and Atlas best practices." },
      { icon: Server, title: "Express APIs", description: "REST or GraphQL with auth, validation, and rate limits." },
      { icon: Code2, title: "React frontend", description: "Typed React with Redux Toolkit, Zustand or TanStack Query." },
      { icon: Shield, title: "JWT + RBAC", description: "Secure authentication and role-based access control." },
      { icon: Zap, title: "Realtime ready", description: "Socket.IO for chat, notifications and live dashboards." },
      { icon: GitBranch, title: "Containerized", description: "Docker, GitHub Actions and one-click deploys." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Domain modeling, user roles, and integration list." },
      { step: "02", title: "API design", description: "Endpoint contracts, schemas, and auth flows." },
      { step: "03", title: "Build", description: "Parallel frontend + backend sprints with weekly demos." },
      { step: "04", title: "Launch", description: "Load testing, observability and runbooks." },
    ],
    deliverables: ["Express API with docs", "React app with state management", "MongoDB schemas + seeds", "Docker compose & CI/CD", "Postman collection", "30 days support"],
    techStack: ["MongoDB", "Express", "React", "Node.js", "TypeScript", "Socket.IO", "JWT", "Docker", "Atlas"],
    pricing: [
      { name: "MVP", price: "$8k", features: ["Auth + 1 core flow", "REST API", "Admin panel", "4 weeks"] },
      { name: "Full app", price: "$22k", period: "project", highlighted: true, features: ["Multi-role", "Realtime", "Payments", "8–12 weeks"] },
      { name: "Retainer", price: "$6k", period: "month", features: ["Bug fixes", "New features", "On-call hours"] },
    ],
    faqs: [
      { q: "Why MERN over Next.js?", a: "Pick MERN when you need a clearly separated API consumed by web, mobile or third parties." },
      { q: "Can you migrate from another stack?", a: "Yes — we routinely port PHP, Rails and .NET apps to MERN incrementally." },
    ],
  },
  "laravel-php": {
    eyebrow: "Web Development · Stacks",
    title: "Laravel / PHP Development",
    sub: "Robust Laravel applications with clean architecture and beautiful admin panels.",
    metaDesc: "Laravel and PHP web development: APIs, admin panels, queues, and integrations built for long-term maintainability.",
    intro:
      "We build Laravel apps that age well. Eloquent models with clear boundaries, jobs and queues for heavy work, Livewire or Inertia for slick interfaces, and Filament-powered admin panels your team will actually enjoy using.",
    features: [
      { icon: FileCode, title: "Clean Laravel", description: "Service classes, form requests, and tested boundaries." },
      { icon: Database, title: "Eloquent done right", description: "Indexed queries, eager loading, and N+1 prevention." },
      { icon: Layers, title: "Filament admin", description: "Production-grade admin panels in a fraction of the time." },
      { icon: Plug, title: "Queues & jobs", description: "Redis-backed jobs, scheduled tasks, and horizon dashboards." },
      { icon: Shield, title: "Sanctum / Passport", description: "Token and OAuth flows for SPAs and mobile apps." },
      { icon: Zap, title: "Octane-ready", description: "Optional Octane setup for high-concurrency workloads." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Domain mapping, user roles, and integration audit." },
      { step: "02", title: "Architecture", description: "Models, jobs, events, and admin panel layout." },
      { step: "03", title: "Build", description: "Weekly demos with seed data and feature flags." },
      { step: "04", title: "Launch", description: "Forge or Vapor deployment, monitoring, backups." },
    ],
    deliverables: ["Laravel app + tests", "Filament admin panel", "API documentation", "Deployment + queues", "Backup strategy", "30 days support"],
    techStack: ["Laravel 11", "PHP 8.3", "Filament", "Livewire", "Inertia", "MySQL", "Redis", "Horizon", "Forge"],
    pricing: [
      { name: "Admin tool", price: "$4k", features: ["CRUD + auth", "Filament UI", "Roles & perms", "3 weeks"] },
      { name: "SaaS / portal", price: "$16k", period: "project", highlighted: true, features: ["Billing", "Multi-tenant", "API", "8–10 weeks"] },
      { name: "Retainer", price: "$5.5k", period: "month", features: ["Maintenance", "New features", "Performance work"] },
    ],
    faqs: [
      { q: "Livewire, Inertia or Blade?", a: "Inertia + React for app-like UIs, Livewire for fast admin and forms, Blade for content sites." },
      { q: "Can you upgrade legacy PHP?", a: "Yes — we handle Laravel 5→11 migrations and PHP 7 → 8.3 upgrades." },
    ],
  },
  "wordpress": {
    eyebrow: "Web Development · Stacks",
    title: "WordPress Development",
    sub: "Custom WordPress themes, plugins, and headless builds — fast and secure.",
    metaDesc: "Custom WordPress development: themes, plugins, WooCommerce, and headless WordPress with Next.js.",
    intro:
      "We build WordPress sites you can actually trust — custom themes built on ACF or a block-based foundation, hardened security, lightning-fast hosting, and the option to go fully headless with React or Next.js when you outgrow the classic stack.",
    features: [
      { icon: Code2, title: "Custom themes", description: "Block themes, ACF-driven layouts, or fully bespoke builds." },
      { icon: ShoppingBag, title: "WooCommerce", description: "Storefronts, custom checkout, subscriptions and integrations." },
      { icon: Zap, title: "Performance tuned", description: "Cloudflare, object caching, image optimization, Core Web Vitals." },
      { icon: Shield, title: "Hardened security", description: "WAF, brute-force protection, file integrity monitoring." },
      { icon: Server, title: "Headless ready", description: "WPGraphQL or REST powering a Next.js or Astro frontend." },
      { icon: Wrench, title: "Editor experience", description: "Custom blocks and patterns your team can actually use." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Content model, editor workflows, and integrations." },
      { step: "02", title: "Design system", description: "Block patterns, ACF fields, and editor previews." },
      { step: "03", title: "Build", description: "Theme/plugin development with staging walkthroughs." },
      { step: "04", title: "Launch", description: "Migration, redirects, security hardening, monitoring." },
    ],
    deliverables: ["Custom theme or headless build", "ACF / block patterns", "Performance + security setup", "Content migration", "Editor docs", "30 days support"],
    techStack: ["WordPress", "WooCommerce", "ACF Pro", "WPGraphQL", "Bedrock", "Cloudflare", "Next.js (headless)", "MariaDB", "Redis"],
    pricing: [
      { name: "Brochure site", price: "$3.5k", features: ["Up to 10 pages", "Custom blocks", "SEO + analytics", "3 weeks"] },
      { name: "WooCommerce", price: "$9k", period: "project", highlighted: true, features: ["Custom checkout", "Payments", "Shipping rules", "6 weeks"] },
      { name: "Care plan", price: "$450", period: "month", features: ["Updates", "Backups", "Uptime + security", "Priority fixes"] },
    ],
    faqs: [
      { q: "Block theme or classic?", a: "New sites use block themes by default. We still build classic themes for complex existing setups." },
      { q: "Should I go headless?", a: "Headless makes sense when you need a SPA-style UX or share content with mobile/other surfaces." },
    ],
  },
  "saas-platforms": {
    eyebrow: "Web Development · Solutions",
    title: "SaaS Platforms",
    sub: "Multi-tenant SaaS products with billing, auth, and admin built-in.",
    metaDesc: "Custom SaaS platforms with multi-tenant architecture, Stripe billing, RBAC, and admin dashboards.",
    intro:
      "We build SaaS products from blank canvas to first paying customer — multi-tenant data isolation, Stripe billing with usage metering, RBAC and team management, polished admin tooling, and the analytics you need to grow.",
    features: [
      { icon: Users, title: "Multi-tenant", description: "Row-level security, team workspaces and invitations." },
      { icon: Package, title: "Stripe billing", description: "Subscriptions, usage-based pricing, and customer portal." },
      { icon: Shield, title: "RBAC + SSO", description: "Roles, permissions, and SAML/OIDC for enterprise plans." },
      { icon: Activity, title: "Product analytics", description: "Funnel, retention, and feature flag instrumentation." },
      { icon: Gauge, title: "Admin dashboard", description: "Internal tools to support customers without touching the DB." },
      { icon: Plug, title: "API + webhooks", description: "Public API and outgoing webhooks for integrations." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Pricing model, personas, and core jobs-to-be-done." },
      { step: "02", title: "Architecture", description: "Tenancy, billing events, and feature flag plan." },
      { step: "03", title: "Build", description: "Weekly slices: auth → core flow → billing → admin." },
      { step: "04", title: "Launch", description: "Beta cohort, instrumentation, and iteration loop." },
    ],
    deliverables: ["Multi-tenant SaaS app", "Stripe billing + portal", "Admin dashboard", "Public API + docs", "Analytics + flags", "30 days support"],
    techStack: ["Next.js / TanStack Start", "TypeScript", "Supabase / Postgres", "Stripe", "Clerk / Supabase Auth", "PostHog", "Resend", "Cloudflare"],
    pricing: [
      { name: "MVP", price: "$18k", features: ["Auth + 1 core flow", "Stripe billing", "Basic admin", "8 weeks"] },
      { name: "Production SaaS", price: "$45k", period: "project", highlighted: true, features: ["Multi-tenant", "RBAC + SSO", "API + webhooks", "12–16 weeks"] },
      { name: "Growth retainer", price: "$10k", period: "month", features: ["Dedicated team", "Weekly releases", "Roadmap planning"] },
    ],
    faqs: [
      { q: "How do you handle tenancy?", a: "Shared schema with RLS by default. Schema-per-tenant or DB-per-tenant when compliance requires it." },
      { q: "Stripe or Paddle?", a: "Stripe for most. Paddle when you want them to be merchant of record for global tax." },
    ],
  },
  "marketing-sites": {
    eyebrow: "Web Development · Solutions",
    title: "Marketing Sites",
    sub: "Conversion-focused marketing sites that load instantly and rank.",
    metaDesc: "High-performance marketing websites with SEO, A/B testing, CMS, and analytics built for conversion.",
    intro:
      "We design and ship marketing sites built for one thing: turning visitors into pipeline. Sub-second loads, Core Web Vitals in the green, structured data for SEO, a CMS your marketing team will actually use, and A/B testing baked in.",
    features: [
      { icon: Rocket, title: "Conversion-first", description: "Above-the-fold patterns proven across hundreds of launches." },
      { icon: Zap, title: "100/100 Lighthouse", description: "Edge rendering, image optimization, and font diet." },
      { icon: Globe, title: "Built for SEO", description: "Per-route metadata, JSON-LD, sitemaps, and OG images." },
      { icon: FileCode, title: "Headless CMS", description: "Sanity, Contentful or Payload — preview + roles included." },
      { icon: Activity, title: "Analytics + A/B", description: "GA4, PostHog, and split testing wired from day one." },
      { icon: Layers, title: "Design system", description: "Tokens and components your team can compose forever." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Audience, message hierarchy, and conversion goals." },
      { step: "02", title: "Design", description: "Visual direction, page system, and motion language." },
      { step: "03", title: "Build", description: "Static + edge-rendered pages with CMS wiring." },
      { step: "04", title: "Launch", description: "SEO migration, analytics, A/B test framework." },
    ],
    deliverables: ["Marketing site (8–20 pages)", "Headless CMS + roles", "Design system", "SEO + analytics", "A/B testing setup", "30 days support"],
    techStack: ["Next.js / Astro", "Sanity / Payload", "Tailwind CSS", "Framer Motion", "PostHog", "GA4", "Cloudflare"],
    pricing: [
      { name: "Starter", price: "$5k", features: ["Up to 8 pages", "CMS-ready", "SEO + analytics", "3 weeks"] },
      { name: "Growth", price: "$15k", period: "project", highlighted: true, features: ["Up to 25 pages", "Blog + resources", "A/B testing", "6 weeks"] },
      { name: "CRO retainer", price: "$5k", period: "month", features: ["Monthly tests", "New pages", "Analytics reviews"] },
    ],
    faqs: [
      { q: "Webflow or custom?", a: "Custom Next.js when you need performance, design freedom and reusable components long-term." },
      { q: "Will my team be able to edit?", a: "Yes — headless CMS with previews so editors see exactly what will go live." },
    ],
  },
  "web-portals": {
    eyebrow: "Web Development · Solutions",
    title: "Web Portals",
    sub: "Customer, partner, and employee portals with secure access and workflows.",
    metaDesc: "Custom web portals: customer self-service, partner networks, and employee dashboards with secure SSO.",
    intro:
      "We build portals where customers find answers without calling support, partners manage their pipeline, and employees stop juggling five tools. SSO, role-based access, document workflows, and integrations into the systems you already run.",
    features: [
      { icon: Building2, title: "Customer self-service", description: "Billing, tickets, documents, and account management." },
      { icon: Users, title: "Partner portals", description: "Deal registration, co-marketing assets, and commissions." },
      { icon: Shield, title: "SSO + RBAC", description: "SAML, OIDC, and granular role-based permissions." },
      { icon: FileCode, title: "Document workflows", description: "Uploads, e-signatures, audit trails, and approvals." },
      { icon: Plug, title: "System integrations", description: "Salesforce, HubSpot, NetSuite, SAP, and custom APIs." },
      { icon: Activity, title: "Usage reporting", description: "Per-account dashboards and exportable reports." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "User roles, workflows, and integration audit." },
      { step: "02", title: "Architecture", description: "Auth model, data flows, and integration patterns." },
      { step: "03", title: "Build", description: "Role-by-role releases with stakeholder demos." },
      { step: "04", title: "Launch", description: "Rollout plan, training, and support handoff." },
    ],
    deliverables: ["Portal app with SSO", "Role-based dashboards", "Document workflows", "Integrations + webhooks", "Admin tooling", "30 days support"],
    techStack: ["Next.js / TanStack Start", "TypeScript", "Postgres", "Auth0 / Clerk", "Stripe", "Salesforce", "HubSpot", "AWS"],
    pricing: [
      { name: "Light portal", price: "$12k", features: ["Auth + 1 role", "Core dashboard", "Basic uploads", "6 weeks"] },
      { name: "Full portal", price: "$35k", period: "project", highlighted: true, features: ["Multi-role + SSO", "Workflows", "Integrations", "10–14 weeks"] },
      { name: "Enterprise", price: "Custom", features: ["SLA", "Dedicated team", "SAML / SCIM", "Quarterly roadmap"] },
    ],
    faqs: [
      { q: "Can it integrate with Salesforce?", a: "Yes — bi-directional sync, embedded views, or full SF Connect setups." },
      { q: "Do you support SCIM provisioning?", a: "Yes, for enterprise plans we ship SCIM and audit logs out of the box." },
    ],
  },
  "progressive-web-apps": {
    eyebrow: "Web Development · Solutions",
    title: "Progressive Web Apps",
    sub: "Installable, offline-capable web apps that feel native.",
    metaDesc: "Progressive Web Apps with offline support, push notifications, and installable native-like experiences.",
    intro:
      "We build PWAs that install to the home screen, work offline, send push notifications, and feel as smooth as native apps — without the app store overhead. One codebase, instant updates, full device reach.",
    features: [
      { icon: Smartphone, title: "Installable", description: "Add-to-home-screen on iOS, Android, Windows and macOS." },
      { icon: Wifi, title: "Offline-first", description: "Service workers with smart caching and background sync." },
      { icon: Activity, title: "Push notifications", description: "Web push on Android and desktop, APNs bridge for iOS." },
      { icon: Zap, title: "App-shell speed", description: "Sub-second cold start with cached shell and lazy data." },
      { icon: Shield, title: "Secure by default", description: "HTTPS, secure cookies, and Trusted Web Activity ready." },
      { icon: Plug, title: "Device APIs", description: "Camera, geolocation, file system, and Web Bluetooth where supported." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Offline scenarios, device APIs, and platform targets." },
      { step: "02", title: "Architecture", description: "Service worker, caching strategy, and sync model." },
      { step: "03", title: "Build", description: "Iterative builds tested across real devices." },
      { step: "04", title: "Launch", description: "Lighthouse PWA pass, store wrap if needed." },
    ],
    deliverables: ["Installable PWA", "Service worker + offline mode", "Push notifications", "Device API integrations", "App store wrappers (optional)", "30 days support"],
    techStack: ["React", "Workbox", "Vite PWA", "Web Push", "IndexedDB", "Capacitor (optional)", "TWA"],
    pricing: [
      { name: "PWA wrapper", price: "$4k", features: ["Add to existing app", "Offline shell", "Install prompt", "2 weeks"] },
      { name: "Full PWA", price: "$18k", period: "project", highlighted: true, features: ["Offline sync", "Push", "Device APIs", "6–8 weeks"] },
      { name: "PWA + stores", price: "$28k", period: "project", features: ["iOS + Android wraps", "Store submission", "Native bridges"] },
    ],
    faqs: [
      { q: "Do PWAs work on iOS?", a: "Yes, with some limitations on push and background sync. We design around them or use Capacitor for full parity." },
      { q: "Will it be in the app stores?", a: "Optional — we can wrap with Capacitor or TWA and handle submissions for you." },
    ],
  },
  "api-integration": {
    eyebrow: "Web Development · Services",
    title: "API Integration",
    sub: "Reliable integrations with third-party APIs, webhooks, and internal systems.",
    metaDesc: "Third-party API integrations, webhook plumbing, and internal system connectors with retries and observability.",
    intro:
      "We connect your app to the rest of your stack — payments, CRMs, ERPs, marketing tools, AI providers, internal systems. Built with retries, idempotency, signed webhooks and the observability you need to trust the pipes.",
    features: [
      { icon: Plug, title: "REST & GraphQL", description: "Typed clients, schema generation, and version pinning." },
      { icon: Shield, title: "Secure webhooks", description: "Signature verification, replay protection, and idempotency." },
      { icon: Activity, title: "Observability", description: "Per-call logs, traces, and alert thresholds." },
      { icon: GitBranch, title: "Retries + DLQ", description: "Exponential backoff and dead-letter queues for failures." },
      { icon: Database, title: "Data sync", description: "Two-way sync, change capture, and conflict resolution." },
      { icon: Rocket, title: "AI gateways", description: "OpenAI, Anthropic, Gemini routing with fallbacks." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Map systems, owners, rate limits, and error modes." },
      { step: "02", title: "Design", description: "Integration patterns, queues, and observability plan." },
      { step: "03", title: "Build", description: "Implement, test against sandboxes, dogfood in staging." },
      { step: "04", title: "Operate", description: "Dashboards, runbooks, and on-call handoff." },
    ],
    deliverables: ["Integration code + tests", "Signed webhook handlers", "Retry + DLQ infrastructure", "Observability dashboards", "Runbooks", "30 days support"],
    techStack: ["TypeScript", "Node.js", "Zod", "BullMQ / SQS", "Stripe", "Salesforce", "HubSpot", "OpenAI", "Datadog"],
    pricing: [
      { name: "Single API", price: "$2.5k", features: ["Auth + core flows", "Webhook handler", "Basic monitoring", "1–2 weeks"] },
      { name: "Multi-system", price: "$9k", period: "project", highlighted: true, features: ["3–5 systems", "Queues + retries", "Dashboards", "3–5 weeks"] },
      { name: "Integration retainer", price: "$4k", period: "month", features: ["Maintenance", "New connectors", "On-call hours"] },
    ],
    faqs: [
      { q: "Which integration platforms do you use?", a: "Custom code by default; Workato, Zapier or n8n when speed matters more than control." },
      { q: "How do you handle rate limits?", a: "Token bucket clients, queue-based smoothing, and per-tenant fairness." },
    ],
  },
  "performance-audits": {
    eyebrow: "Web Development · Services",
    title: "Performance Audits",
    sub: "Find what's slow, fix what matters, and prove the wins.",
    metaDesc: "Web performance audits with Core Web Vitals analysis, fixes, and measurable speed improvements.",
    intro:
      "We profile your site or app end-to-end — Core Web Vitals, network waterfall, JS bundle, server response, database queries — then prioritize fixes by user impact and ship the ones that move the needle.",
    features: [
      { icon: Gauge, title: "Core Web Vitals", description: "LCP, INP, CLS analysis across real-user and lab data." },
      { icon: Zap, title: "Bundle diet", description: "Code splitting, tree-shaking, and dependency audits." },
      { icon: Globe, title: "Edge + CDN", description: "Caching strategy, image optimization, and edge rendering." },
      { icon: Database, title: "DB + API tuning", description: "Index analysis, N+1 fixes, and response shape review." },
      { icon: Activity, title: "RUM dashboards", description: "Real-user monitoring with budgets and alerts." },
      { icon: Wrench, title: "Fix playbook", description: "Prioritized backlog with effort/impact estimates." },
    ],
    process: [
      { step: "01", title: "Baseline", description: "Capture metrics from RUM, lab, and field data." },
      { step: "02", title: "Audit", description: "Profile frontend, network, server, and database." },
      { step: "03", title: "Fix", description: "Ship the highest-impact changes with measurements." },
      { step: "04", title: "Monitor", description: "Set budgets, alerts, and a regression-prevention plan." },
    ],
    deliverables: ["Audit report with prioritized fixes", "Implementation of top wins", "RUM dashboard + budgets", "Before/after metrics", "Team walkthrough", "30 days support"],
    techStack: ["Lighthouse", "WebPageTest", "Chrome DevTools", "Sentry / Datadog", "PostHog", "Vercel Analytics"],
    pricing: [
      { name: "Audit only", price: "$2k", features: ["Full report", "Top 10 fixes", "1-hour review call", "1 week"] },
      { name: "Audit + fix", price: "$8k", period: "project", highlighted: true, features: ["Audit", "Top fixes shipped", "RUM setup", "3 weeks"] },
      { name: "Performance retainer", price: "$3.5k", period: "month", features: ["Monthly review", "Continuous fixes", "Regression alerts"] },
    ],
    faqs: [
      { q: "How much faster will my site be?", a: "Typical wins: 40–70% reduction in LCP and INP within the first sprint." },
      { q: "Do you work with WordPress / Shopify?", a: "Yes — we tune themes, plugins, and Liquid alongside the infra layer." },
    ],
  },
  "maintenance-support": {
    eyebrow: "Web Development · Services",
    title: "Maintenance & Support",
    sub: "Keep your site fast, secure, and shipping — without hiring full-time.",
    metaDesc: "Ongoing web maintenance and support: updates, monitoring, backups, security patches, and on-demand fixes.",
    intro:
      "We become the engineering team you call when something breaks at 11pm — and the one that makes sure it doesn't. Updates, backups, monitoring, security patches, performance tuning, and a steady stream of small improvements.",
    features: [
      { icon: Shield, title: "Security patches", description: "Dependency updates, CVE monitoring, and quick fixes." },
      { icon: Activity, title: "24/7 monitoring", description: "Uptime, errors, performance, and SLA alerts." },
      { icon: Database, title: "Backups + DR", description: "Automated backups, restore drills, and DR runbooks." },
      { icon: Wrench, title: "Bug fixes", description: "Same-week turnaround on prioritized issues." },
      { icon: GitBranch, title: "Small features", description: "Continuous improvements baked into the retainer." },
      { icon: Gauge, title: "Quarterly reviews", description: "Performance, security, and roadmap reviews each quarter." },
    ],
    process: [
      { step: "01", title: "Onboarding", description: "Audit, access, runbooks, and SLA agreement." },
      { step: "02", title: "Baseline", description: "Monitoring, backups, alerts, and dashboards." },
      { step: "03", title: "Operate", description: "Continuous patching, fixes, and small features." },
      { step: "04", title: "Review", description: "Quarterly health, security, and roadmap reviews." },
    ],
    deliverables: ["Monitoring + alerts", "Backup + restore drills", "Monthly patching", "Bug fix capacity", "Quarterly health report", "Documented runbooks"],
    techStack: ["Sentry", "Datadog", "Better Stack", "Cloudflare", "Dependabot", "GitHub Actions"],
    pricing: [
      { name: "Essentials", price: "$650", period: "month", features: ["Updates + backups", "Uptime monitoring", "Email support", "4h dev time"] },
      { name: "Growth", price: "$1.8k", period: "month", highlighted: true, features: ["All Essentials", "Performance tuning", "Priority bugs", "12h dev time"] },
      { name: "Enterprise", price: "Custom", features: ["SLA + on-call", "Dedicated engineer", "Quarterly roadmap", "Unlimited fixes"] },
    ],
    faqs: [
      { q: "What's response time?", a: "Critical issues within 1 hour on Growth+, next business day on Essentials." },
      { q: "Can you take over from another agency?", a: "Yes — we handle the handover audit and document everything within the first 2 weeks." },
    ],
  },
};

export const Route = createFileRoute("/services/web-development/$child")({
  component: ChildPage,
  loader: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ params }) => {
    const entry = CHILDREN[params.child];
    if (!entry) return {};
    const url = `/services/web-development/${params.child}`;
    return {
      meta: [
        { title: `${entry.title} — CoderLity` },
        { name: "description", content: entry.metaDesc },
        { property: "og:title", content: `${entry.title} — CoderLity` },
        { property: "og:description", content: entry.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

function ChildPage() {
  const { entry } = Route.useLoaderData();
  return <ServicePage data={entry} />;
}
